// Client/src/pages/digi/Invoice/Components/BillingGroupsTab.jsx
//
// A vendor's property groups: named sets of buildings the company pays for
// together ("Oak Tower & Spruce Heights"), billed on one invoice. Vendor-only.
// The buildings offered are exactly the ones the invoice form offers
// (/eligibility), so a group can never name a building the form would refuse.

import { useState, useEffect, useCallback, useMemo } from "react";
import { Paper, Tooltip } from "@mui/material";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Spinner } from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { groupBuildingsByProperty } from "./invoiceFormGroups";
import {
  listBillingGroups,
  createBillingGroup,
  updateBillingGroup,
  archiveBillingGroup,
  restoreBillingGroup,
  listTemplates,
} from "./templateApi";

const ACCENT = "#4F46E5";
const MAX_NAME = 80;
const MAX_BILL_TO = 500;

const btn = (variant = "primary") => ({
  display: "inline-flex", alignItems: "center", gap: 6, minHeight: 36,
  borderRadius: 8, padding: "7px 14px", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
  ...(variant === "primary"
    ? { background: ACCENT, color: "#fff", border: "none", boxShadow: "0 2px 8px rgba(79,70,229,0.25)" }
    : variant === "danger"
      ? { background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA" }
      : { background: "#fff", color: "#334155", border: "1px solid #E2E8F0" }),
});

const selectStyles = {
  control: (base, { isFocused }) => ({
    ...base, borderRadius: 8, minHeight: 38, fontSize: "0.84rem",
    borderColor: isFocused ? ACCENT : "#E2E8F0",
    boxShadow: isFocused ? "0 0 0 3px rgba(79,70,229,0.12)" : "none",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const keyOf = (propertyId, buildingName) => `${propertyId}::${String(buildingName).trim().toLowerCase()}`;

// ── Create / edit dialog ─────────────────────────────────────────────────

const GroupDialog = ({ isOpen, group, properties, templates, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [billTo, setBillTo] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [picked, setPicked] = useState(new Map()); // key -> { propertyId, buildingName }
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(group?.name || "");
    setBillTo(group?.billTo || "");
    setTemplateId(group?.templateId ? String(group.templateId) : "");
    const m = new Map();
    (group?.buildings || []).forEach((b) => m.set(keyOf(b.propertyId, b.buildingName), {
      propertyId: String(b.propertyId), buildingName: b.buildingName,
    }));
    setPicked(m);
    setSearch("");
    setTouched(false);
  }, [isOpen, group]);

  // All buildings of a group must belong to one company: once one is picked,
  // the other companies' properties are shown disabled.
  const companyOf = (pid) => String(properties.find((p) => String(p._id) === String(pid))?.companyId || "");
  const lockedCompany = useMemo(() => {
    const first = picked.values().next().value;
    return first ? companyOf(first.propertyId) : "";
  }, [picked]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (properties || [])
      .map((p) => ({ ...p, buildings: (p.buildings || []).filter(Boolean) }))
      .filter((p) => p.buildings.length)
      .filter((p) => !q || String(p.title).toLowerCase().includes(q) ||
        p.buildings.some((b) => String(b).toLowerCase().includes(q)))
      .sort((a, b) => String(a.title).localeCompare(String(b.title)));
  }, [properties, search]);

  const toggle = (pid, bname) => setPicked((prev) => {
    const next = new Map(prev);
    const k = keyOf(pid, bname);
    if (next.has(k)) next.delete(k); else next.set(k, { propertyId: String(pid), buildingName: bname });
    return next;
  });

  const toggleAll = (p, on) => setPicked((prev) => {
    const next = new Map(prev);
    p.buildings.forEach((b) => {
      const k = keyOf(p._id, b);
      if (on) next.set(k, { propertyId: String(p._id), buildingName: b });
      else next.delete(k);
    });
    return next;
  });

  const nameError = !name.trim() ? "Give the group a name" : name.trim().length > MAX_NAME ? `At most ${MAX_NAME} characters` : "";
  const buildingsError = picked.size ? "" : "Pick at least one building";
  const billToError = billTo.length > MAX_BILL_TO ? `At most ${MAX_BILL_TO} characters` : "";
  const invalid = !!(nameError || buildingsError || billToError);

  const templateOptions = [
    { value: "", label: "My default template" },
    ...(templates || []).filter((t) => !t.isArchived).map((t) => ({
      value: String(t._id),
      label: `${t.name}${(t.approvals || []).some((a) => a.approvedVersion > 0) ? "" : " (not approved yet)"}`,
    })),
  ];

  const save = async () => {
    setTouched(true);
    if (invalid) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        billTo: billTo.trim(),
        templateId: templateId || null,
        buildings: Array.from(picked.values()),
      };
      const saved = group?._id ? await updateBillingGroup(group._id, payload) : await createBillingGroup(payload);
      toast.success(group?._id ? "Property group saved" : "Property group created");
      onSaved(saved);
    } catch {
      // toast shown by the api helper
    } finally {
      setSaving(false);
    }
  };

  const err = (msg) => touched && msg
    ? <div role="alert" style={{ color: "#DC2626", fontSize: "0.74rem", marginTop: 4 }}>{msg}</div>
    : null;

  return (
    <Modal isOpen={isOpen} toggle={() => !saving && onClose()} size="lg" centered scrollable>
      <ModalHeader toggle={() => !saving && onClose()}>
        {group?._id ? "Edit property group" : "New property group"}
      </ModalHeader>
      <ModalBody style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <Label for="bg-name" style={{ fontWeight: 700, fontSize: "0.8rem" }}>
            Group name <span style={{ color: "#DC2626" }}>*</span>
          </Label>
          <Input id="bg-name" value={name} maxLength={MAX_NAME + 20} placeholder="e.g. Oak Tower & Spruce Heights"
            onChange={(e) => setName(e.target.value)} invalid={touched && !!nameError} />
          {err(nameError)}
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
            <Label style={{ fontWeight: 700, fontSize: "0.8rem", marginBottom: 4 }}>
              Buildings <span style={{ color: "#DC2626" }}>*</span>
            </Label>
            <span aria-live="polite" style={{ fontSize: "0.75rem", color: "#475569", fontWeight: 600 }}>
              {picked.size} selected
            </span>
          </div>
          <div style={{ fontSize: "0.74rem", color: "#64748B", marginBottom: 8 }}>
            Pick the buildings you bill together on one invoice. They must all belong to the same company.
          </div>
          <div style={{ position: "relative", marginBottom: 8 }}>
            <FeatherIcon icon="search" size={14} color="#94A3B8"
              style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }} />
            <Input aria-label="Search buildings" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search properties or buildings" style={{ paddingLeft: 32 }} />
          </div>
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, maxHeight: 320, overflowY: "auto" }}>
            {!visible.length && (
              <div style={{ padding: 18, textAlign: "center", color: "#94A3B8", fontSize: "0.82rem" }}>
                {properties.length ? "No building matches your search." : "You have no buildings to invoice yet."}
              </div>
            )}
            {visible.map((p) => {
              const otherCompany = lockedCompany && String(p.companyId || "") !== lockedCompany;
              const allOn = p.buildings.every((b) => picked.has(keyOf(p._id, b)));
              const someOn = p.buildings.some((b) => picked.has(keyOf(p._id, b)));
              return (
                <div key={p._id} style={{ borderBottom: "1px solid #F1F5F9", padding: "10px 14px", opacity: otherCompany ? 0.5 : 1 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: "0.83rem", color: "#0F172A", cursor: otherCompany ? "not-allowed" : "pointer", margin: 0 }}>
                    <input type="checkbox" checked={allOn} disabled={otherCompany}
                      ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                      onChange={(e) => toggleAll(p, e.target.checked)} />
                    {p.title}
                    {p.buildings.length > 1 && (
                      <span style={{ fontWeight: 500, color: "#94A3B8", fontSize: "0.72rem" }}>
                        all {p.buildings.length} buildings
                      </span>
                    )}
                    {otherCompany && (
                      <span style={{ fontWeight: 500, color: "#94A3B8", fontSize: "0.72rem" }}>· another company</span>
                    )}
                  </label>
                  {p.buildings.length > 1 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", padding: "6px 0 0 24px" }}>
                      {p.buildings.map((b) => (
                        <label key={b} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.8rem", color: "#334155", margin: 0, cursor: otherCompany ? "not-allowed" : "pointer" }}>
                          <input type="checkbox" disabled={otherCompany} checked={picked.has(keyOf(p._id, b))}
                            onChange={() => toggle(p._id, b)} />
                          {b}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {err(buildingsError)}
        </div>

        <div>
          <Label for="bg-billto" style={{ fontWeight: 700, fontSize: "0.8rem" }}>Bill To (optional)</Label>
          <Input id="bg-billto" type="textarea" rows={3} value={billTo} onChange={(e) => setBillTo(e.target.value)}
            placeholder={"Leave blank to bill the company by name.\nOr enter the paying entity and address."} />
          <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginTop: 4 }}>
            Prints as “Bill To” on invoices for this group.
          </div>
          {err(billToError)}
        </div>

        <div>
          <Label style={{ fontWeight: 700, fontSize: "0.8rem" }}>Invoice template</Label>
          <Select
            inputId="bg-template"
            options={templateOptions}
            value={templateOptions.find((o) => o.value === templateId) || templateOptions[0]}
            onChange={(o) => setTemplateId(o?.value || "")}
            styles={selectStyles}
            menuPortalTarget={document.body}
          />
          <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginTop: 4 }}>
            A different template for this group only. It is used once the company approves it.
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button type="button" style={btn("secondary")} onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" style={{ ...btn("primary"), opacity: saving ? 0.7 : 1 }} onClick={save} disabled={saving}>
          {saving ? <Spinner size="sm" style={{ width: 14, height: 14 }} /> : <FeatherIcon icon="check" size={14} />}
          {group?._id ? "Save group" : "Create group"}
        </button>
      </ModalFooter>
    </Modal>
  );
};

// ── Tab ──────────────────────────────────────────────────────────────────

const BillingGroupsTab = ({ eligibility }) => {
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState(null); // null | {} (new) | group
  const [busyId, setBusyId] = useState(null);

  const properties = eligibility?.properties || [];
  const canCreate = !!eligibility?.canCreate;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, t] = await Promise.all([
        listBillingGroups(showArchived ? { includeArchived: "1" } : {}),
        listTemplates().catch(() => []),
      ]);
      setGroups(Array.isArray(g) ? g : []);
      setTemplates(Array.isArray(t) ? t : []);
    } catch {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const setActive = async (group, active) => {
    if (!active) {
      const res = await Swal.fire({
        title: `Archive “${group.name}”?`,
        text: "It disappears from the invoice form. Invoices already sent keep the group name.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Archive",
        confirmButtonColor: "#DC2626",
      });
      if (!res.isConfirmed) return;
    }
    setBusyId(group._id);
    try {
      await (active ? restoreBillingGroup(group._id) : archiveBillingGroup(group._id));
      toast.success(active ? "Group restored" : "Group archived");
      load();
    } catch {
      // toast shown by the api helper
    } finally {
      setBusyId(null);
    }
  };

  const templateName = (id) => templates.find((t) => String(t._id) === String(id))?.name;

  return (
    <>
      <Paper elevation={0} className="iv-wrap iv-paper">
        <div className="iv-header">
          <div className="iv-header__left">
            <div className="iv-header__icon-wrap">
              <FeatherIcon icon="layers" size={18} color="#A5B4FC" />
            </div>
            <div className="iv-header__meta">
              <div className="iv-header__label">Invoices</div>
              <div className="iv-header__title">Property Groups</div>
            </div>
            {groups.length > 0 && (
              <div className="iv-header__badges">
                <span className="iv-header__badge iv-header__badge--records">
                  {groups.length} group{groups.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "#475569", margin: 0, cursor: "pointer" }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Show archived
            </label>
            <Tooltip title={canCreate ? "" : (eligibility?.reason || "You are not set up to invoice yet.")} arrow>
              <span>
                <button type="button" style={{ ...btn("primary"), opacity: canCreate ? 1 : 0.5 }}
                  disabled={!canCreate} onClick={() => setDialog({})}>
                  <FeatherIcon icon="plus" size={14} /> New group
                </button>
              </span>
            </Tooltip>
          </div>
        </div>

        <div style={{ padding: 16 }}>
          {loading && (
            <div style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: ACCENT }} /></div>
          )}

          {!loading && !groups.length && (
            <div className="iv-empty" style={{ minHeight: "30vh" }}>
              <div className="iv-empty__icon-wrap"><FeatherIcon icon="layers" size={24} color={ACCENT} /></div>
              <div className="iv-empty__title">No property groups yet</div>
              <div className="iv-empty__sub" style={{ maxWidth: 460 }}>
                Bill several buildings on one invoice — for example the buildings one owner pays for.
                Pick a group on a new invoice and only its buildings are offered.
              </div>
              {canCreate && (
                <button type="button" style={{ ...btn("primary"), marginTop: 14 }} onClick={() => setDialog({})}>
                  <FeatherIcon icon="plus" size={14} /> Create your first group
                </button>
              )}
            </div>
          )}

          {!loading && groups.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
              {groups.map((g) => (
                <div key={g._id} style={{
                  border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, background: g.isActive ? "#fff" : "#F8FAFC",
                  display: "flex", flexDirection: "column", gap: 10,
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, color: "#0F172A", fontSize: "0.92rem", overflowWrap: "anywhere" }}>{g.name}</div>
                      <div style={{ fontSize: "0.74rem", color: "#64748B", marginTop: 2 }}>
                        {(g.buildings || []).length} building{(g.buildings || []).length !== 1 ? "s" : ""}
                        {!g.isActive && " · archived"}
                      </div>
                    </div>
                    {!g.isActive && <span className="iv-chip iv-chip--gray">Archived</span>}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {groupBuildingsByProperty(g, properties).map((p) => (
                      <div key={p.title} style={{ fontSize: "0.78rem", color: "#334155" }}>
                        <span style={{ fontWeight: 700 }}>{p.title}</span>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 3 }}>
                          {p.buildings.map((b) => <span key={b} className="iv-chip iv-chip--indigo">{b}</span>)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ fontSize: "0.76rem", color: "#475569", display: "grid", gap: 4 }}>
                    <div>
                      <span style={{ color: "#94A3B8", fontWeight: 600 }}>Bill To: </span>
                      <span style={{ whiteSpace: "pre-wrap" }}>{g.billTo || "the company, by name"}</span>
                    </div>
                    <div>
                      <span style={{ color: "#94A3B8", fontWeight: 600 }}>Template: </span>
                      {g.templateId ? (templateName(g.templateId) || "a template") : "your default"}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, marginTop: "auto", paddingTop: 4 }}>
                    {g.isActive ? (
                      <>
                        <button type="button" style={btn("secondary")} onClick={() => setDialog(g)}>
                          <FeatherIcon icon="edit-2" size={13} /> Edit
                        </button>
                        <button type="button" style={btn("danger")} disabled={busyId === g._id} onClick={() => setActive(g, false)}>
                          <FeatherIcon icon="archive" size={13} /> Archive
                        </button>
                      </>
                    ) : (
                      <button type="button" style={btn("secondary")} disabled={busyId === g._id} onClick={() => setActive(g, true)}>
                        <FeatherIcon icon="rotate-ccw" size={13} /> Restore
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Paper>

      <GroupDialog
        isOpen={!!dialog}
        group={dialog && dialog._id ? dialog : null}
        properties={properties}
        templates={templates}
        onClose={() => setDialog(null)}
        onSaved={() => { setDialog(null); load(); }}
      />
    </>
  );
};

export { BillingGroupsTab };
